import type { JSX } from 'react';
import type { QuestionOption, WireConversationSummary } from '@logistics/contracts';
import { now } from '@/lib/clock';
import { cn } from '@/lib/utils';
import { monoLabel } from '../shell/look';
import { conversationMeta } from './chat-format';
import { ScopeChips } from './scope-chips';
import type { ScopeOption } from './use-chat-data';

/**
 * The left column: "+ New conversation", the RECENT list (everyone's for an
 * admin, so each row names its owner), and the SEARCH IN chips.
 */
export function ConversationList({
  meId,
  items,
  activeId,
  onNew,
  onOpen,
  onDelete,
  more,
  scopes,
  scope,
  onToggleScope,
  className,
}: {
  meId: string;
  items: readonly WireConversationSummary[];
  activeId: string | null;
  onNew: () => void;
  onOpen: (id: string) => void;
  onDelete: (conversation: WireConversationSummary) => void;
  more: { has: boolean; loading: boolean; load: () => void; status?: string | undefined };
  scopes: readonly ScopeOption[];
  scope: readonly QuestionOption[];
  onToggleScope: (option: QuestionOption) => void;
  className?: string | undefined;
}): JSX.Element {
  const at = now();
  return (
    <aside
      className={cn('border-line bg-bg flex h-full min-h-0 min-w-0 flex-col border-r', className)}
    >
      <div className="flex flex-none flex-col gap-2.5 px-3.5 pt-4 pb-2.5">
        <button
          type="button"
          onClick={onNew}
          className="border-line bg-surf text-ink hover:border-acc flex items-center gap-2 rounded-[10px] border px-3 py-2.5 text-[13.5px] font-medium transition-colors"
        >
          <span className="text-acc text-base leading-none">+</span>New conversation
        </button>
      </div>
      <div className={`${monoLabel} px-3.5 pb-2`}>RECENT</div>
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-3">
        {more.status !== undefined && (
          <div className="text-mute px-2.5 py-2 text-xs">{more.status}</div>
        )}
        {items.map((c) => (
          <Row
            key={c.id}
            conversation={c}
            active={c.id === activeId}
            meta={conversationMeta(c, meId, at)}
            onOpen={() => onOpen(c.id)}
            onDelete={c.owner.id === meId ? () => onDelete(c) : undefined}
          />
        ))}
        {more.has && (
          <button
            type="button"
            onClick={more.load}
            disabled={more.loading}
            className="text-acc hover:text-ink mt-1 border-0 bg-transparent px-2.5 py-2 text-left text-xs"
          >
            {more.loading ? 'Loading…' : 'Show older'}
          </button>
        )}
      </div>
      <div className="border-line flex-none border-t px-3.5 py-3">
        <div className={`${monoLabel} mb-2`}>SEARCH IN</div>
        <ScopeChips scopes={scopes} scope={scope} onToggle={onToggleScope} />
      </div>
    </aside>
  );
}

function Row({
  conversation,
  active,
  meta,
  onOpen,
  onDelete,
}: {
  conversation: WireConversationSummary;
  active: boolean;
  meta: string;
  onOpen: () => void;
  onDelete: (() => void) | undefined;
}): JSX.Element {
  return (
    <div
      className="group hover:bg-surf2 relative rounded-[9px] transition-colors"
      style={{ background: active ? 'var(--surf2)' : undefined }}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full min-w-0 flex-col gap-[3px] rounded-[9px] border-0 bg-transparent px-2.5 py-[9px] pr-8 text-left text-inherit"
      >
        <span
          className="w-full truncate text-[13px] font-medium"
          style={{ color: active ? 'var(--ink)' : 'var(--mute)' }}
        >
          {conversation.title}
        </span>
        <span className="text-mute font-mono text-[10.5px]">{meta}</span>
      </button>
      {onDelete !== undefined && (
        <button
          type="button"
          onClick={onDelete}
          title="Delete conversation"
          aria-label={`Delete ${conversation.title}`}
          className="text-mute hover:text-bad absolute top-2 right-1.5 grid size-6 place-items-center rounded-md border-0 bg-transparent text-sm opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
        >
          ×
        </button>
      )}
    </div>
  );
}
